import { PaginatedResponse } from '@copilot/data/responses/interface';
import { CampaignModel } from '@copilot/common/pages/campaigns/data/models';

type CampaignsLoadAction = {
    type: 'LOAD_CAMPAIGNS';
};

type CampaignsLoadSuccessAction = {
    type: 'CAMPAIGNS_LOADED';
    payload: PaginatedResponse<CampaignModel>;
};

type CampaignsLoadSuccessWithFilterAction<Filters> = {
    type: 'CAMPAIGNS_LOADED_WITH_FILTER';
    payload: PaginatedResponse<CampaignModel>;
    filter: Filters;
    searchTerm: string;
};

type CampaignsLoadErrorAction = {
    type: 'CAMPAIGNS_LOAD_FAILED';
    error: any;
};

type CampaignLoadAction = {
    type: 'CAMPAIGN_LOADED';
    payload: CampaignModel;
};

type CampaignLoadErrorAction = {
    type: 'CAMPAIGN_LOAD_FAILED';
    error: any;
};

/**
 * Load action.  Initiates loading of campaigns.
 */
export const load = (): CampaignsLoadAction => ({ type: 'LOAD_CAMPAIGNS' });

/**
 * Load Success action, dispatched when loading of campaigns was successful.
 * @param payload
 */
export const loadSuccess = (
    payload: PaginatedResponse<CampaignModel>
): CampaignsLoadSuccessAction => ({ type: 'CAMPAIGNS_LOADED', payload });

/**
 * Load error action, dispatched when loading of campaigns failed.
 * @param error
 */
export const loadError = (error: unknown): CampaignsLoadErrorAction => ({
    type: 'CAMPAIGNS_LOAD_FAILED',
    error,
});

/**
 * Load success action of one campaign, dispatched when loading of one campaign was successful.
 * @param payload
 */
export const loadOneSuccess = (payload: CampaignModel): CampaignLoadAction => ({
    type: 'CAMPAIGN_LOADED',
    payload,
});

/**
 * Load error action of one campaign, dispatched when loading of one campaign failed.
 * @param error
 */
export const loadOneError = (error: unknown): CampaignLoadErrorAction => ({
    type: 'CAMPAIGN_LOAD_FAILED',
    error,
});

export const loadSuccessWithFilter = <Filters>(
    payload: PaginatedResponse<CampaignModel>,
    filter: Filters,
    searchTerm: string
): CampaignsLoadSuccessWithFilterAction<Filters> => ({
    type: 'CAMPAIGNS_LOADED_WITH_FILTER',
    payload,
    filter,
    searchTerm,
});

/**
 * The action type for fetching campaigns.
 */
export const FetchCampaignsActionType = 'FETCH_CAMPAIGNS';
/**
 * The action to initiate loading of campaigns.
 */
export type FetchCampaignsAction = {
    type: typeof FetchCampaignsActionType;
    organizationId: string;
    pageNumber: number;
    pageSize: number;
    searchTerm?: string;
};

/**
 * Initiates loading of campaigns.
 * @param organizationId
 * @param pageNumber
 * @param pageSize
 */
export const fetchCampaigns = (
    organizationId: string,
    pageNumber: number,
    pageSize: number,
    searchTerm?: string
): FetchCampaignsAction => ({
    type: FetchCampaignsActionType,
    organizationId,
    pageNumber,
    pageSize,
    searchTerm,
});

//#region Filters
export const CampaignsUpdateFilterActionType = 'CAMPAIGNS_FILTER_UPDATE';

/**
 * The action to update campaigns filter
 */
export type CampaignsUpdateFilterAction<Filters> = {
    type: typeof CampaignsUpdateFilterActionType;
    filter: Filters;
    orgId: string;
    searchTerm: string;
};

//#endregion Filters

/**
 * The type of campaign action.  It's a union of different types of actions to be handled by the reducer - very useful in reducer to write type safe code.
 */
export type CampaignAction<T> =
    | CampaignsLoadAction
    | CampaignsLoadSuccessAction
    | CampaignsLoadErrorAction
    | CampaignLoadAction
    | CampaignsUpdateFilterAction<T>
    | CampaignsLoadSuccessWithFilterAction<T>;
